/*
  NOTE: THIS ONLY WORKS IN PRODUCTION.
  In development, weirdly, the path is always `undefined`, and nothing shows up. But in production, it’s fine.
*/

'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { normalize } from '@/utils';
import useWindowDimensions from '@/hooks/use-window-dimensions';

interface Props {
  choiceRefs: React.RefObject<{
    [index: number]: HTMLElement;
  }>;
  choiceIndex: number;
  connectionStyle?: 'centered' | 'locked';
  dependency?: any;
}

function Wire({
  choiceRefs,
  choiceIndex,
  connectionStyle = 'centered',
  dependency = [],
}: Props) {
  const windowDimensions = useWindowDimensions();

  const [path, setPath] = React.useState('');

  const svgRef = React.useRef<SVGSVGElement>(null);

  React.useEffect(() => {
    // Bail out if any elements are missing
    const choiceNode = choiceRefs.current?.[choiceIndex];
    if (!choiceNode || !svgRef.current) {
      return;
    }

    const svgBB = svgRef.current.getBoundingClientRect();
    const choiceBB = choiceNode.getBoundingClientRect();

    const BUMP_BY = 20;

    const cX = svgBB.left - 4;

    const startPoint = {
      x: 0,
      y: connectionStyle === 'centered' ? svgBB.height / 2 : 90,
    };

    // So our SVG is floating somewhere in the viewport. So is the
    // choice. Because SVGs use an internal coordinate system, I
    // need to figure out the choice's `y` coordinate by getting
    // the choice's bounding box relative to the SVG
    const endPoint = {
      x: choiceBB.left - cX - 16,
      y:
        connectionStyle === 'centered'
          ? choiceBB.top - svgBB.top + choiceBB.height / 2 - 2
          : choiceBB.top - svgBB.top + 26,
    };

    const startBump = normalize(
      choiceIndex,
      0,
      Object.keys(choiceRefs.current).length,
      BUMP_BY * 0.1,
      BUMP_BY * 0.1 * -1
    );

    const middleBump = normalize(
      choiceIndex,
      0,
      Object.keys(choiceRefs.current).length,
      BUMP_BY,
      BUMP_BY * -1
    );

    // To keep things fun, pick a dynamic "midpoint" to use, so that
    // the arrow becomes more/less curvy each time
    const midpoint = svgBB.width * 0.5;

    const path = `
      M ${startPoint.x} ${startPoint.y + startBump}
      C ${startPoint.x + midpoint} ${startPoint.y + middleBump}
        ${endPoint.x - midpoint} ${endPoint.y - 30}
        ${endPoint.x} ${endPoint.y}
      Q ${endPoint.x + 6} ${endPoint.y + 4}
        ${endPoint.x - 2} ${endPoint.y + 8}
      C ${endPoint.x - 8} ${endPoint.y + 12}
        ${endPoint.x - 22} ${endPoint.y}
        ${endPoint.x - 3} ${endPoint.y - 3}
    `;

    setPath(path);
  }, [
    choiceIndex,
    choiceRefs,
    connectionStyle,
    windowDimensions.width,
    dependency,
  ]);

  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      ref={svgRef}
      width="100%"
      height="100%"
      fill="none"
    >
      {path && (
        <motion.path
          animate={{ d: path }}
          transition={{ type: 'spring', damping: 10, stiffness: 100 }}
        />
      )}
    </Svg>
  );
}

const Svg = styled.svg`
  display: block;
  overflow: visible;

  path {
    stroke: var(--color-secondary);
    stroke-width: 3px;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export default Wire;
